// ---------------------------------------------------------------------------
// pricing-section.content.ts — Translated content for PricingSection
// ---------------------------------------------------------------------------
import type { SupportedLocale, LocaleContentMap } from '../i18n.config';

export interface PricingSectionContent {
  title: string;
  subtitle: string;
  mostPopular: string;
  getStarted: string;
  perMonth: string;
  plans: Array<{ name: string; tagline: string; features: string[] }>;
}

const en: PricingSectionContent = {
  title: 'Simple, Transparent Pricing',
  subtitle: 'No hidden fees. Cancel anytime. 14-day free trial included.',
  mostPopular: 'Most Popular',
  getStarted: 'Get Started',
  perMonth: '/mo',
  plans: [
    { name: 'Starter', tagline: 'Perfect for small hotels', features: ['Up to 30 rooms', 'Basic analytics', 'Email support', '1 user', 'Standard reports'] },
    { name: 'Professional', tagline: 'For growing properties', features: ['Up to 100 rooms', 'Advanced analytics', 'Priority support', '10 users', 'Custom reports', 'Channel manager', 'POS integration'] },
    { name: 'Enterprise', tagline: 'For hotel chains', features: ['Unlimited rooms', 'AI analytics', '24/7 phone support', 'Unlimited users', 'All integrations', 'Multi-property', 'White label', 'API access'] },
  ],
};

const ar: PricingSectionContent = {
  title: 'أسعار بسيطة وشفافة',
  subtitle: 'بدون رسوم مخفية. إلغاء في أي وقت. تجربة مجانية لمدة ١٤ يوماً.',
  mostPopular: 'الأكثر شعبية',
  getStarted: 'ابدأ الآن',
  perMonth: '/شهر',
  plans: [
    { name: 'المبتدئ', tagline: 'مثالي للفنادق الصغيرة', features: ['حتى ٣٠ غرفة', 'تحليلات أساسية', 'دعم بالبريد الإلكتروني', 'مستخدم واحد', 'تقارير قياسية'] },
    { name: 'الاحترافي', tagline: 'للعقارات النامية', features: ['حتى ١٠٠ غرفة', 'تحليلات متقدمة', 'دعم ذو أولوية', '١٠ مستخدمين', 'تقارير مخصصة', 'مدير القنوات', 'تكامل نقاط البيع'] },
    { name: 'المؤسسات', tagline: 'لسلاسل الفنادق', features: ['غرف غير محدودة', 'تحليلات بالذكاء الاصطناعي', 'دعم هاتفي ٢٤/٧', 'مستخدمون غير محدودين', 'جميع التكاملات', 'إدارة متعددة العقارات', 'العلامة البيضاء', 'وصول API'] },
  ],
};

const th: PricingSectionContent = {
  title: 'ราคาที่เรียบง่ายและโปร่งใส',
  subtitle: 'ไม่มีค่าธรรมเนียมแอบแฝง ยกเลิกได้ทุกเมื่อ ทดลองใช้ฟรี 14 วัน',
  mostPopular: 'ยอดนิยม',
  getStarted: 'เริ่มต้นใช้งาน',
  perMonth: '/เดือน',
  plans: [
    { name: 'สตาร์ทเตอร์', tagline: 'เหมาะสำหรับโรงแรมขนาดเล็ก', features: ['สูงสุด 30 ห้อง', 'วิเคราะห์ข้อมูลพื้นฐาน', 'สนับสนุนทางอีเมล', '1 ผู้ใช้', 'รายงานมาตรฐาน'] },
    { name: 'โปรเฟสชันนัล', tagline: 'สำหรับธุรกิจที่กำลังเติบโต', features: ['สูงสุด 100 ห้อง', 'วิเคราะห์ข้อมูลขั้นสูง', 'สนับสนุนลำดับความสำคัญ', '10 ผู้ใช้', 'รายงานที่กำหนดเอง', 'ตัวจัดการช่องทาง', 'การรวม POS'] },
    { name: 'เอ็นเตอร์ไพรส์', tagline: 'สำหรับเครือโรงแรม', features: ['ห้องไม่จำกัด', 'วิเคราะห์ด้วย AI', 'สนับสนุนทางโทรศัพท์ 24/7', 'ผู้ใช้ไม่จำกัด', 'การรวมทั้งหมด', 'หลายสาขา', 'แบรนด์ส่วนตัว', 'เข้าถึง API'] },
  ],
};

const es: PricingSectionContent = {
  title: 'Precios Simples y Transparentes',
  subtitle: 'Sin tarifas ocultas. Cancela en cualquier momento. Prueba gratuita de 14 días incluida.',
  mostPopular: 'Más Popular',
  getStarted: 'Comenzar',
  perMonth: '/mes',
  plans: [
    { name: 'Inicial', tagline: 'Perfecto para hoteles pequeños', features: ['Hasta 30 habitaciones', 'Analítica básica', 'Soporte por email', '1 usuario', 'Informes estándar'] },
    { name: 'Profesional', tagline: 'Para propiedades en crecimiento', features: ['Hasta 100 habitaciones', 'Analítica avanzada', 'Soporte prioritario', '10 usuarios', 'Informes personalizados', 'Gestor de canales', 'Integración POS'] },
    { name: 'Empresarial', tagline: 'Para cadenas hoteleras', features: ['Habitaciones ilimitadas', 'Analítica con IA', 'Soporte telefónico 24/7', 'Usuarios ilimitados', 'Todas las integraciones', 'Multi-propiedad', 'Marca blanca', 'Acceso API'] },
  ],
};

const fr: PricingSectionContent = {
  title: 'Tarification Simple et Transparente',
  subtitle: 'Pas de frais cachés. Annulez à tout moment. Essai gratuit de 14 jours inclus.',
  mostPopular: 'Le Plus Populaire',
  getStarted: 'Commencer',
  perMonth: '/mois',
  plans: [
    { name: 'Débutant', tagline: 'Parfait pour les petits hôtels', features: ['Jusqu\'à 30 chambres', 'Analytique de base', 'Support par email', '1 utilisateur', 'Rapports standards'] },
    { name: 'Professionnel', tagline: 'Pour les établissements en croissance', features: ['Jusqu\'à 100 chambres', 'Analytique avancée', 'Support prioritaire', '10 utilisateurs', 'Rapports personnalisés', 'Gestionnaire de canaux', 'Intégration POS'] },
    { name: 'Entreprise', tagline: 'Pour les chaînes hôtelières', features: ['Chambres illimitées', 'Analytique IA', 'Support téléphonique 24/7', 'Utilisateurs illimités', 'Toutes les intégrations', 'Multi-propriété', 'Marque blanche', 'Accès API'] },
  ],
};

const de: PricingSectionContent = {
  title: 'Einfache, Transparente Preise',
  subtitle: 'Keine versteckten Gebühren. Jederzeit kündbar. 14-tägige kostenlose Testversion inklusive.',
  mostPopular: 'Am Beliebtesten',
  getStarted: 'Jetzt Starten',
  perMonth: '/Monat',
  plans: [
    { name: 'Starter', tagline: 'Perfekt für kleine Hotels', features: ['Bis zu 30 Zimmer', 'Basis-Analytik', 'E-Mail-Support', '1 Benutzer', 'Standardberichte'] },
    { name: 'Professional', tagline: 'Für wachsende Betriebe', features: ['Bis zu 100 Zimmer', 'Erweiterte Analytik', 'Prioritäts-Support', '10 Benutzer', 'Individuelle Berichte', 'Channel-Manager', 'POS-Integration'] },
    { name: 'Enterprise', tagline: 'Für Hotelketten', features: ['Unbegrenzte Zimmer', 'KI-Analytik', '24/7 Telefon-Support', 'Unbegrenzte Benutzer', 'Alle Integrationen', 'Multi-Property', 'White Label', 'API-Zugang'] },
  ],
};

const ja: PricingSectionContent = {
  title: 'シンプルで透明な料金プラン',
  subtitle: '隠れた手数料なし。いつでもキャンセル可能。14日間の無料トライアル付き。',
  mostPopular: '一番人気',
  getStarted: '始める',
  perMonth: '/月',
  plans: [
    { name: 'スターター', tagline: '小規模ホテルに最適', features: ['最大30室', '基本分析', 'メールサポート', '1ユーザー', '標準レポート'] },
    { name: 'プロフェッショナル', tagline: '成長中の施設向け', features: ['最大100室', '高度な分析', '優先サポート', '10ユーザー', 'カスタムレポート', 'チャネルマネージャー', 'POS連携'] },
    { name: 'エンタープライズ', tagline: 'ホテルチェーン向け', features: ['無制限の客室', 'AI分析', '24時間電話サポート', '無制限のユーザー', '全統合機能', 'マルチプロパティ', 'ホワイトラベル', 'APIアクセス'] },
  ],
};

const zh: PricingSectionContent = {
  title: '简单透明的定价',
  subtitle: '无隐藏费用。随时取消。包含14天免费试用。',
  mostPopular: '最受欢迎',
  getStarted: '立即开始',
  perMonth: '/月',
  plans: [
    { name: '入门版', tagline: '适合小型酒店', features: ['最多30间客房', '基础分析', '邮件支持', '1位用户', '标准报表'] },
    { name: '专业版', tagline: '适合成长中的酒店', features: ['最多100间客房', '高级分析', '优先支持', '10位用户', '自定义报表', '渠道管理器', 'POS集成'] },
    { name: '企业版', tagline: '适合连锁酒店', features: ['无限客房', 'AI分析', '24/7电话支持', '无限用户', '全部集成', '多物业管理', '白标', 'API访问'] },
  ],
};

const hi: PricingSectionContent = {
  title: 'सरल, पारदर्शी मूल्य निर्धारण',
  subtitle: 'कोई छिपी फीस नहीं। कभी भी रद्द करें। 14-दिन का मुफ़्त ट्रायल शामिल।',
  mostPopular: 'सबसे लोकप्रिय',
  getStarted: 'शुरू करें',
  perMonth: '/माह',
  plans: [
    { name: 'स्टार्टर', tagline: 'छोटे होटलों के लिए', features: ['30 कमरे तक', 'बेसिक एनालिटिक्स', 'ईमेल सपोर्ट', '1 उपयोगकर्ता', 'स्टैंडर्ड रिपोर्ट'] },
    { name: 'प्रोफेशनल', tagline: 'बढ़ते होटलों के लिए', features: ['100 कमरे तक', 'एडवांस एनालिटिक्स', 'प्रायोरिटी सपोर्ट', '10 उपयोगकर्ता', 'कस्टम रिपोर्ट', 'चैनल मैनेजर', 'POS इंटीग्रेशन'] },
    { name: 'एंटरप्राइज़', tagline: 'होटल चेन के लिए', features: ['असीमित कमरे', 'AI एनालिटिक्स', '24/7 फोन सपोर्ट', 'असीमित उपयोगकर्ता', 'सभी इंटीग्रेशन', 'मल्टी-प्रॉपर्टी', 'व्हाइट लेबल', 'API एक्सेस'] },
  ],
};

export const PRICING_SECTION_CONTENT: LocaleContentMap<PricingSectionContent> = { en, ar, th, es, fr, de, ja, zh, hi };
